import type { ComponentType } from "react";

import EcfaLanding from "./ecfa/EcfaLanding";

/* =============================================================================
   Custom organization landing pages.

   One folder per partner. An organization with an entry here gets its own
   landing page at /<slug>; an organization without one falls back to the
   generic PublicLanding — the same page "/" uses — so adding an org never
   requires writing a page first.

   What belongs in a partner folder: their words, their sections, their order.
   What does NOT: pricing (OrgPlans renders the platform's live tiers), auth
   doors, or anything that touches listing data. A partner page is marketing
   over the shared product, never a second implementation of it.

   To add one:
     1. src/orgs/<slug>/content.ts   — the copy, apart from the layout
     2. src/orgs/<slug>/XLanding.tsx — the page, taking OrgLandingProps
     3. one line in CUSTOM_LANDINGS below, keyed by the org's slug

   Imported eagerly: partner pages are small and the app ships as one bundle
   today. If this grows past a handful, switch the map's values to React.lazy
   and wrap the render site in Suspense — the shape here is already right for it.
   ========================================================================== */

/** The org record a custom landing receives, resolved by OrgLanding. */
export interface OrgLandingProps {
  org: {
    id: number;
    name: string;
    slug: string;
    /** Copy fields from the org record. These exist to customise the GENERIC
     *  landing; a custom page normally ignores them and speaks in its own
     *  voice from its content file. Passed through anyway so a page can opt
     *  in where it makes sense. */
    headline: string | null;
    subheadline: string | null;
    description: string | null;
    logoUrl: string | null;
    websiteUrl: string | null;
    /** Platform plan names this org offers, in display order. */
    tierNames: string[];
  };
}

/** slug → custom landing page. Slugs must match orgs.slug exactly. */
const CUSTOM_LANDINGS: Record<string, ComponentType<OrgLandingProps>> = {
  ecfa: EcfaLanding,
};

/** The custom landing for a slug, or null to fall back to PublicLanding. */
export function customLandingFor(slug: string): ComponentType<OrgLandingProps> | null {
  return CUSTOM_LANDINGS[slug.toLowerCase()] ?? null;
}
